"use client";

import snakeCase from "lodash/snakeCase";

import Carousel from "@/components/explore-sections/shared/carousel";
import ImageComponent from "@/components/ui/image";
import BakersHero1 from "@/assets/images/bakers_hero1.webp";
import { useMediaQuery } from "react-responsive";
import { useRouter } from "next/navigation";
import { useMemo, useEffect } from "react";
import { getCollectionById } from "@/services/api/collections";

import SectionHeading from "@/components/explore-sections/shared/heading";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { HAND_MADE_IN_INDIA_COLLECTION_ID } from "@/constants/product";
import { sendGAEvent } from "@next/third-parties/google";
import { HomeSectionSkeleton } from "@/components/ui/skeleton";
import UniversalButton from "@/components/common/UniversalButton";

interface BestGift {
  id: string | number;
  image: string;
  description: string;
  vendor: string;
  slug: string;
}

interface CollectionProduct {
  id?: string | number;
  slug?: string;
  vendor_details?: { slug?: string };
  product_urls?: string[];
  name?: string;
  [key: string]: unknown;
}

export default function BestGifts() {
  const isMobile = useMediaQuery({ maxWidth: 1023 });
  const router = useRouter();
  const { data, isError, isLoading } = useQuery({
    queryKey: ["collection", HAND_MADE_IN_INDIA_COLLECTION_ID.toString()],
    queryFn: () =>
      getCollectionById(HAND_MADE_IN_INDIA_COLLECTION_ID.toString()),
  });

  const bestgifts = useMemo(
    () =>
      (data?.products?.results as CollectionProduct[])
        ?.filter((product) => product.slug && product.vendor_details?.slug)
        ?.map((product) => ({
          id: product.id,
          image: product.product_urls?.[0] || BakersHero1,
          description: product.name || "Product",
          vendor: product.vendor_details.slug,
          slug: product.slug,
        }))
        ?.slice(0, 8) || [],
    [data?.products?.results],
  );

  let itemsPerPage = 4;
  if (isMobile) itemsPerPage = 2;

  const handleItemClick = (item: BestGift) => {
    sendGAEvent("event", "best_of_gifts_item_clicked", {
      item_name: item.description,
      item_id: String(item.id),
      vendor_name: item.vendor,
    });
    router.push(`/${item.vendor}/product/${item.slug}`);
  };

  useEffect(() => {
    if (isError) {
      toast.error("Error fetching best gifts");
    }
  }, [isError]);

  if (isError) {
    return null;
  }

  if (isLoading) {
    return <HomeSectionSkeleton />;
  }

  if (!data || !data.slug || !data.id) {
    return null;
  }

  return (
    <div
      className={`mt-8 px-2 lg:mt-12 lg:px-12 max-w-[1440px] mx-auto md:hidden`}
    >
      <SectionHeading
        heading={data?.name || "The Best of Gifts"}
        subHeading={"Share joy through gifts that win every heart."}
        link={`/listing/${data.slug.toLocaleLowerCase()}?id=${data.id}`}
        onClick={() =>
          sendGAEvent(
            "event",
            `show_more_${snakeCase(data?.name || "")}_clicked`,
          )
        }
      />
      <p className="font-outfit font-normal lg:hidden text-center text-sm text-v2-on-surface-variant/80 uppercase tracking-widest mt-2">
        Made in India. Made by hand.
      </p>
      <div className="snap-x snap-mandatory w-full mt-8 overflow-x-auto">
        <Carousel
          items={bestgifts}
          {...(!isMobile && { itemsPerPage })}
          renderItem={(item, idx) => (
            <div
              key={idx}
              className={`group flex flex-col justify-start items-center w-full ${
                isMobile
                  ? "aspect-square w-[40%] h-[210px] me-2.5"
                  : "flex-none w-[calc((100%-72px)/4)] h-[330px]"
              } cursor-pointer`}
              onClick={() => handleItemClick(item)}
              onMouseEnter={() =>
                router.prefetch(`/${item.vendor}/product/${item.slug}`)
              }
            >
              <div className="rounded w-full h-[220px] sm:rounded-lg sm:h-[280px] md:h-[280px] transition-transform duration-300 ease-in-out relative overflow-hidden">
                <ImageComponent
                  src={item.image}
                  alt={item.description}
                  width={400}
                  height={400}
                  sizes="(max-width: 640px) 300px, (max-width: 1024px) 200px, 360px"
                  className="w-full h-full object-cover object-center transition-transform duration-300 ease-in-out hover:scale-105 group-hover:scale-105"
                  fallbackSrc={BakersHero1}
                />
              </div>
              <h3 className="line-clamp-1 overflow-hidden text-ellipsis whitespace-normal text-center w-full font-lora text-[#303030] group-hover:text-[#735c00] mt-2 text-base font-medium leading-6 lg:text-left lg:text-xl lg:leading-7">
                {item.description}
              </h3>
            </div>
          )}
        />
      </div>
      <div className="flex justify-center items-center w-full py-4 lg:pt-0">
        <UniversalButton
          text=" VIEW ALL"
          classNames="lg:text-xl font-lora font-extrabold bg-[#037A6A] text-base text-[#F3F3F3] lg:px-10 lg:py-4 px-3 py-2 rounded-lg lg:hidden"
          onClick={() => {
            sendGAEvent("event", "show_more_best_of_gifts_clicked");
            router.push(
              `/listing/${data.slug.toLocaleLowerCase()}?id=${data.id}`,
            );
          }}
          variant="custom"
        />
      </div>
    </div>
  );
}
